import { cn } from '@/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * RDS Card 공통 그림자 (Context Menu / Pop-Up과 동일).
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=270-9889
 */
export const cardShadowClassName =
  'shadow-[0_6px_14px_-6px_rgba(19,25,39,0.12),0_10px_32px_-4px_rgba(19,25,39,0.1)]';

export const cardVariants = cva(
  [
    'overflow-hidden bg-white text-ruda-text-primary',
    'border-0',
    cardShadowClassName,
  ],
  {
    variants: {
      /** Horizontal Small = 20 / Vertical = 32 */
      size: {
        md: 'rounded-20',
        lg: 'rounded-32',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export type CardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants>;

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ size }), className)}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-1 px-6 pt-6', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-base font-bold leading-6 tracking-tight text-ruda-text-primary',
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'text-sm font-normal leading-[22px] tracking-tight text-ruda-text-secondary',
      className,
    )}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6', className)} {...props} />
));
CardContent.displayName = 'CardContent';

/** 미디어(이미지) 영역 — Vertical 카드 상단 */
const CardMedia = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('relative w-full overflow-hidden bg-ruda-grey-50', className)}
    {...props}
  />
));
CardMedia.displayName = 'CardMedia';

/**
 * 하단 액션 영역 — RDS Vertical은 border 없이 버튼 스택(gap-16).
 */
const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col gap-4 px-6 pb-6 pt-0 [&_button]:w-full',
      className,
    )}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardMedia,
  CardTitle,
};
